import { SECTION_META, prettifySegment } from '@/lib/docSections';
import type { FaqEntry } from '@/lib/faqs';

export const ORIGIN = 'https://axionquant.com';

const ORG_REF = {
  '@type': 'Organization',
  name: 'AxionQuant',
  url: `${ORIGIN}/`,
};

/** Friendly labels for product routes (breadcrumb + dataset naming). */
const PRODUCT_LABELS: Record<string, string> = {
  stocks: 'Stocks & Equities',
  etfs: 'ETFs & Funds',
  forex: 'Currencies & FX',
  futures: 'Futures Markets',
  indices: 'Market Indices',
  crypto: 'Crypto Markets',
  'corporate-profiles': 'Corporate Intelligence',
  earnings: 'Earnings & Transcripts',
  economics: 'Economic Indicators & Data',
  news: 'News & Media Feeds',
  esg: 'ESG Scores',
  credit: 'Credit Ratings & Risk',
  'supply-chain': 'Supply Chain Relationships',
  sentiment: 'Market Sentiment',
  webtraffic: 'Web Traffic Signals',
  disclosures: 'Disclosures & Compliance',
};

/** One-sentence description per product dataset (Google Dataset rich result). */
const PRODUCT_DATASET_DESCRIPTIONS: Record<string, string> = {
  stocks:
    'Daily and hourly stock price data from 85+ global exchanges with OHLC candles and full history back to 1987 for 100,000+ symbols.',
  etfs:
    'ETF data covering daily holdings, fund flows, NAV, expense ratios and factor exposures for 10,000+ funds worldwide.',
  forex:
    'Daily and historical exchange rates for 180+ currency pairs with decades of history, central bank policy and volatility metrics.',
  futures:
    'Daily and historical futures prices for 2,000+ contracts across 25+ exchanges, including commodities, open interest and curve data.',
  indices:
    'Daily and historical data for 50,000+ global market indices with constituents, weights and rebalancing history.',
  crypto:
    'Daily and historical cryptocurrency prices for 10,000+ digital assets across 100+ exchanges, plus on-chain and derivatives data.',
  'corporate-profiles':
    'Company profiles with fundamentals, executive leadership and financial data for 100,000+ public companies.',
  earnings:
    'EPS actuals, estimates, surprises, call transcripts and 8-K tables for 60,000+ companies, built for backtesting.',
  economics:
    '2M+ macroeconomic indicators across 195+ countries: GDP, inflation, employment, trade and central bank policy.',
  news:
    'Financial news from 500+ sources with AI entity extraction and impact scoring, covering company headlines and decades of history.',
  esg:
    'ESG scores and sustainability ratings for 15,000+ companies across environmental, social and governance factors.',
  credit:
    'Credit ratings from S&P, Moody\'s and Fitch plus credit risk and CDS spreads for 50,000+ issuers back to 1987.',
  'supply-chain':
    'Company relationships, supplier networks and trade flows for 25,000+ companies, refreshed daily.',
  sentiment:
    'Market sentiment signals from news, social media and analyst sources across 500+ feeds with daily updates.',
  webtraffic:
    'Website visitor analytics, app downloads and digital presence data for 5,000+ companies as a consumer interest signal.',
  disclosures:
    'Regulatory disclosures and compliance records normalized for screening, monitoring and investment research.',
};

const DATASET_LICENSE = 'https://axionquant.com/terms';

/** `Stocks & Equities` -> `Stocks & Equities Data`, but never `... Data Data`. */
const datasetName = (label: string) =>
  /Data$/.test(label) ? label : `${label} Data`;

interface Crumb {
  name: string;
  url: string;
}

/**
 * Derive a breadcrumb trail from a canonical path. Returns null for pages
 * where a trail adds nothing (homepage, single-level marketing pages).
 */
export const buildBreadcrumbs = (
  path: string,
  title: string
): Crumb[] | null => {
  const crumbs: Crumb[] = [{ name: 'Home', url: `${ORIGIN}/` }];
  let m: RegExpMatchArray | null;

  if ((m = path.match(/^\/docs\/?(.*)$/))) {
    crumbs.push({ name: 'Docs', url: `${ORIGIN}/docs` });
    const rest = m[1].replace(/\/+$/, '');
    if (!rest) return null; // /docs itself is one level under Home
    let acc = '';
    for (const seg of rest.split('/').filter(Boolean)) {
      acc += `/${seg}`;
      const key = acc.replace(/^\//, '');
      const label =
        SECTION_META[key]?.label ?? prettifySegment(seg);
      crumbs.push({ name: label, url: `${ORIGIN}/docs${acc}` });
    }
    return crumbs;
  }

  if ((m = path.match(/^\/stock\/([A-Za-z0-9.\-]+)/))) {
    crumbs.push({ name: 'Stocks & Equities', url: `${ORIGIN}/stocks` });
    crumbs.push({ name: m[1].toUpperCase(), url: `${ORIGIN}/stock/${m[1]}` });
    return crumbs;
  }

  if ((m = path.match(/^\/products\/([a-z\-]+)/))) {
    crumbs.push({ name: 'Products', url: `${ORIGIN}/products` });
    crumbs.push({
      name: PRODUCT_LABELS[m[1]] ?? prettifySegment(m[1]),
      url: `${ORIGIN}/products/${m[1]}`,
    });
    return crumbs;
  }

  if ((m = path.match(/^\/blog\/(.+)$/))) {
    crumbs.push({ name: 'Blog', url: `${ORIGIN}/blog` });
    crumbs.push({ name: title.replace(/\s*\|\s*AxionQuant$/, ''), url: `${ORIGIN}/blog/${m[1]}` });
    return crumbs;
  }

  if (path === '/stocks') {
    crumbs.push({ name: 'Stocks & Equities', url: `${ORIGIN}/stocks` });
    return crumbs;
  }

  if (path === '/pricing') {
    crumbs.push({ name: 'Pricing', url: `${ORIGIN}/pricing` });
    return crumbs;
  }

  if (path === '/compare' || path === '/compare/') {
    crumbs.push({ name: 'Comparisons', url: `${ORIGIN}/compare` });
    return crumbs;
  }

  if ((m = path.match(/^\/compare\/(.+)$/))) {
    crumbs.push({ name: 'Comparisons', url: `${ORIGIN}/compare` });
    crumbs.push({ name: title.replace(/\s*\|\s*AxionQuant$/, ''), url: `${ORIGIN}/compare/${m[1]}` });
    return crumbs;
  }

  if (path === '/about') {
    crumbs.push({ name: 'About', url: `${ORIGIN}/about` });
    return crumbs;
  }

  if (path === '/developers') {
    crumbs.push({ name: 'Developers', url: `${ORIGIN}/developers` });
    return crumbs;
  }

  if (path === '/support') {
    crumbs.push({ name: 'Support', url: `${ORIGIN}/support` });
    return crumbs;
  }

  return null;
};

export const buildBreadcrumbList = (crumbs: Crumb[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: c.url,
  })),
});

export const buildTechArticle = (
  title: string,
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: title,
  description,
  url: canonicalUrl,
  author: ORG_REF,
  publisher: { ...ORG_REF, '@type': 'Organization' },
  isPartOf: {
    '@type': 'WebSite',
    name: 'AxionQuant Documentation',
    url: `${ORIGIN}/docs`,
  },
});

export const buildCollectionPage = (
  title: string,
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: title,
  description,
  url: canonicalUrl,
  isPartOf: {
    '@type': 'WebSite',
    name: 'AxionQuant Documentation',
    url: `${ORIGIN}/docs`,
  },
});

export const buildDataset = (
  title: string,
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: title,
  description,
  url: canonicalUrl,
  creator: ORG_REF,
  keywords: ['financial data API', 'market data', 'quantitative finance'],
  license: DATASET_LICENSE,
});

export const buildOrganizationPage = (
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'AxionQuant',
  alternateName: ['Axion', 'Axion Quant'],
  url: canonicalUrl,
  logo: 'https://axionquant.com/web-app-manifest-512x512.png',
  description,
  foundingDate: '2024',
  sameAs: [
    'https://medium.com/@axionquant',
    'https://www.instagram.com/axionquant/',
    'https://x.com/axionQuant',
    'https://github.com/axionquant',
    'https://www.youtube.com/@AxionQuant',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: `${ORIGIN}/support`,
  },
});

export const buildSoftwareApplication = (
  title: string,
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: title,
  applicationCategory: 'DeveloperApplication',
  applicationSubCategory: 'Financial Market Data API',
  operatingSystem: 'Web-based (REST API)',
  url: canonicalUrl,
  description,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'Free tier: 1,000 API requests/month',
  },
  provider: {
    '@type': 'Organization',
    name: 'AxionQuant',
    url: `${ORIGIN}/`,
  },
  featureList: [
    'REST API with JSON responses',
    'Python, TypeScript, JavaScript, and C++ SDKs',
    'MCP server for AI agents',
    '16+ financial datasets in one API',
    'Real-time and historical market data',
    'Alternative data: sentiment, ESG, supply chain, web traffic',
  ],
});

export const buildProductsCollection = (
  title: string,
  description: string,
  canonicalUrl: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: title,
  description,
  url: canonicalUrl,
  hasPart: Object.entries(PRODUCT_LABELS).map(([slug, label]) => ({
    '@type': 'Dataset',
    name: datasetName(label),
    description: PRODUCT_DATASET_DESCRIPTIONS[slug] ?? label,
    url: `${ORIGIN}/products/${slug}`,
    creator: ORG_REF,
    license: DATASET_LICENSE,
  })),
});

export const buildFaqPage = (faqs: FaqEntry[], canonicalUrl?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  ...(canonicalUrl ? { url: canonicalUrl } : {}),
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
});
